import { registerTextureCacheSizeProvider } from '@utils/debug/webglDiagnostics'
import { Material, SRGBColorSpace, Texture, TextureLoader } from 'three'

/** Albedo maps deben estar en sRGB para PBR correcto (evita texturas lavadas). */
export function configureColorTexture(texture: Texture): void {
  texture.colorSpace = SRGBColorSpace
}

const MAX_CACHED_TEXTURES = 512
const LOG_PREFIX = '[TextureCache]'

const loader = new TextureLoader()

type CacheEntry = {
  texture: Texture
  refCount: number
  lastUsed: number
  status: 'loading' | 'ready'
  pendingLoads: Array<(texture: Texture) => void>
}

const byUrl = new Map<string, CacheEntry>()
const urlByTexture = new WeakMap<Texture, string>()

export function normalizeTextureUrl(url: string): string {
  if (!url) return url
  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.delete('not-from-cache-please')
    return parsed.toString()
  } catch {
    return url.replace(/[?&]not-from-cache-please/, '').replace(/\?$/, '')
  }
}

/** URL de fetch con cache-bust (comportamiento legacy del loader). */
function buildFetchUrl(cacheKey: string): string {
  return cacheKey.includes('?')
    ? `${cacheKey}&not-from-cache-please`
    : `${cacheKey}?not-from-cache-please`
}

function touch(entry: CacheEntry) {
  entry.lastUsed = performance.now()
}

function evictUnreferencedTextures() {
  if (byUrl.size <= MAX_CACHED_TEXTURES) return

  const candidates = [...byUrl.entries()]
    .filter(([, entry]) => entry.refCount <= 0 && entry.status === 'ready')
    .sort((a, b) => a[1].lastUsed - b[1].lastUsed)

  for (const [key, entry] of candidates) {
    if (byUrl.size <= MAX_CACHED_TEXTURES) break
    entry.texture.dispose()
    byUrl.delete(key)
    if (import.meta.env?.DEV) {
      console.debug(`${LOG_PREFIX} evicted texture`)
    }
  }
}

function flushPendingLoads(entry: CacheEntry) {
  const callbacks = entry.pendingLoads.splice(0)
  for (const cb of callbacks) {
    cb(entry.texture)
  }
}

export function retainTexture(texture: Texture | null | undefined) {
  if (!texture) return
  const key = urlByTexture.get(texture)
  if (!key) return
  const entry = byUrl.get(key)
  if (!entry || entry.status !== 'ready') return
  entry.refCount++
  touch(entry)
}

export function releaseTexture(texture: Texture | null | undefined) {
  if (!texture) return

  const key = urlByTexture.get(texture)
  if (!key) {
    texture.dispose()
    return
  }

  const entry = byUrl.get(key)
  if (!entry) {
    texture.dispose()
    return
  }

  entry.refCount = Math.max(0, entry.refCount - 1)
  touch(entry)
  evictUnreferencedTextures()
}

export function setMaterialMap(material: Material, texture: Texture | null) {
  const previous = material.map
  if (previous && previous !== texture) {
    releaseTexture(previous)
    releaseCachedTextureBase(material)
  }
  material.map = texture
  if (texture) {
    retainTexture(texture)
  }
  material.needsUpdate = true
}

/** Libera la referencia al texture base del caché guardada en userData. */
export function releaseCachedTextureBase(material: Material) {
  const base = material.userData?.currentTextureBase as Texture | undefined
  if (base) {
    releaseTexture(base)
    material.userData.currentTextureBase = null
  }
}

/** Textura clonada con repeat/offset propio: retiene el base del caché mientras el clone esté activo. */
export function setMaterialMapUnique(material: Material, texture: Texture, baseTexture?: Texture) {
  const previous = material.map
  if (previous && previous !== texture) {
    releaseTexture(previous)
    releaseCachedTextureBase(material)
  }
  material.map = texture
  if (baseTexture) {
    retainTexture(baseTexture)
    material.userData.currentTextureBase = baseTexture
  }
  material.needsUpdate = true
}

export function getCachedTexture(url: string): Texture | undefined {
  const key = normalizeTextureUrl(url)
  const entry = byUrl.get(key)
  if (!entry || entry.status !== 'ready') return undefined
  touch(entry)
  return entry.texture
}

export function setCachedTexture(url: string, texture: Texture) {
  const key = normalizeTextureUrl(url)
  const existing = byUrl.get(key)
  if (existing) {
    if (existing.texture !== texture) {
      if (existing.status === 'ready') {
        releaseTexture(existing.texture)
      } else {
        existing.texture.dispose()
      }
      existing.texture = texture
      existing.refCount = 0
      existing.status = 'ready'
      existing.pendingLoads = []
    }
    touch(existing)
    urlByTexture.set(texture, key)
    return
  }
  byUrl.set(key, {
    texture,
    refCount: 0,
    lastUsed: performance.now(),
    status: 'ready',
    pendingLoads: [],
  })
  urlByTexture.set(texture, key)
  evictUnreferencedTextures()
}

export function loadCachedTexture(url: string, onLoad: (texture: Texture) => void): Texture | null {
  const key = normalizeTextureUrl(url)
  if (!key) return null

  const existing = byUrl.get(key)
  if (existing?.status === 'ready') {
    touch(existing)
    onLoad(existing.texture)
    return existing.texture
  }
  if (existing?.status === 'loading') {
    existing.pendingLoads.push(onLoad)
    return existing.texture
  }

  const fetchUrl = buildFetchUrl(key)
  const pendingLoads: Array<(texture: Texture) => void> = [onLoad]

  const texture = loader.load(
    fetchUrl,
    (loaded) => {
      const entry = byUrl.get(key)
      if (!entry) return
      configureColorTexture(loaded)
      entry.texture = loaded
      entry.status = 'ready'
      touch(entry)
      urlByTexture.set(loaded, key)
      flushPendingLoads(entry)
    },
    undefined,
    (error) => {
      console.error(`${LOG_PREFIX} failed to load`, key, error)
      const entry = byUrl.get(key)
      if (entry) {
        entry.pendingLoads = []
        entry.texture.dispose()
        byUrl.delete(key)
      }
    },
  )

  // Registrar ANTES de que termine la carga para evitar loads duplicados concurrentes.
  byUrl.set(key, {
    texture,
    refCount: 0,
    lastUsed: performance.now(),
    status: 'loading',
    pendingLoads,
  })
  urlByTexture.set(texture, key)
  evictUnreferencedTextures()

  return texture
}

export function getTextureCacheSize(): number {
  return byUrl.size
}

export function clearTextureCache() {
  for (const [, entry] of byUrl) {
    entry.pendingLoads = []
    entry.texture.dispose()
  }
  byUrl.clear()
}

registerTextureCacheSizeProvider(() => getTextureCacheSize())
